import BackdropBlur, {
  type BackdropBlurProps,
} from '@/app/components/BackdropBlur';
import { type MotionProps } from '@/app/components/Motion';

type SectionContentAppUIBackdropVariant = 'light' | 'dark';

export interface SectionContentAppUIBackdropProps
  extends Omit<BackdropBlurProps, 'variant'> {
  readonly isActive?: boolean;
  readonly isLargeBreakpoint?: boolean;
  readonly variant: {
    sm: SectionContentAppUIBackdropVariant;
    lg: SectionContentAppUIBackdropVariant;
  };
}

const getBackdropBlurProps = ({
  variant,
}: Pick<SectionContentAppUIBackdropProps, 'variant'>): Pick<
  BackdropBlurProps,
  'color' | 'opacity'
> => {
  return {
    color: {
      sm: variant.sm === 'light' ? 'light' : 'dark',
      lg: variant.lg === 'light' ? 'light' : 'dark',
    },
    opacity: {
      sm: variant.sm === 'light' ? 0.1 : 1,
      lg: variant.lg === 'light' ? 0.1 : 1,
    },
  };
};

export const getFadeMotionProps = ({
  isActive,
  isLargeBreakpoint,
}: {
  isActive: boolean;
  isLargeBreakpoint: boolean;
}): MotionProps => {
  return isLargeBreakpoint
    ? {
        animate: {
          opacity: isActive ? 1 : 0,
          transitionEnd: {
            opacity: isActive ? 1 : 0,
          },
        },
        initial: {
          opacity: 0,
        },
      }
    : {
        initial: {
          opacity: 0,
          y: 0,
        },
        viewport: {
          once: true,
        },
        whileInView: {
          opacity: 1,
        },
      };
};

const SectionContentAppUIBackdrop = ({
  isActive,
  isLargeBreakpoint,
  variant,
  ...props
}: SectionContentAppUIBackdropProps): JSX.Element => {
  const fadeMotionProps = getFadeMotionProps({
    isActive: Boolean(isActive),
    isLargeBreakpoint: Boolean(isLargeBreakpoint),
  });
  return (
    <BackdropBlur
      {...fadeMotionProps}
      {...getBackdropBlurProps({ variant })}
      {...props}
      className="z-10"
      transition={{ duration: 1 }}
      variant="card"
    />
  );
};

export default SectionContentAppUIBackdrop;
